import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, extractError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=checking, false=anonymous, object=authed
    const [error, setError] = useState(null);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (err) {
            // Anonymous / expired session — surface for debugging without throwing
            if (err?.response?.status && err.response.status !== 401) {
                console.warn("[auth] refreshMe failed:", err);
            }
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => { refreshMe(); }, [refreshMe]);

    const login = useCallback(async (email, password) => {
        setError(null);
        try {
            const normalized = (email || "").trim().toLowerCase();
            const { data } = await api.post("/auth/login", { email: normalized, password });
            setUser(data);
            return data;
        } catch (e) {
            setError(extractError(e));
            throw e;
        }
    }, []);

    const register = useCallback(async (payload) => {
        setError(null);
        try {
            const { data } = await api.post("/auth/register", payload);
            setUser(data);
            return data;
        } catch (e) {
            setError(extractError(e));
            throw e;
        }
    }, []);

    const logout = useCallback(async () => {
        try { await api.post("/auth/logout"); }
        catch (err) { console.warn("[auth] logout request failed (continuing anyway):", err); }
        setUser(false);
    }, []);

    const value = useMemo(
        () => ({ user, error, login, register, logout, refreshMe, setError }),
        [user, error, login, register, logout, refreshMe],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
